"use client";

import { algoliaClient, AlgoliaIndexes, getAlgoliaIndex } from "@wojo/services";
import { Button, Card, Container, Markdown, SearchField, Text } from "@wojo/ui";
import algoliasearchHelper, { SearchResults } from "algoliasearch-helper";
import { useQueryState } from "nuqs";
import { ReactNode, useState } from "react";
import styles from "./EntryRequirementsSearch.module.scss";

export type EntryRequirementsSearchProps = {};

const searchHelper = algoliasearchHelper(
    algoliaClient,
    getAlgoliaIndex(AlgoliaIndexes.TOURPAGES),
    {
        attributesToRetrieve: ["objectID", "tourCode", "_highlightResult"],
        facets: ["businessCode"],
        highlightPreTag: "**",
        highlightPostTag: "**",
        responseFields: ["hits", "nbHits", "hitsPerPage"],
    },
);
searchHelper.addFacetRefinement(
    "businessCode",
    process.env.NEXT_PUBLIC_BUSINESS_CODE || "",
);

export const EntryRequirementsSearch: React.FC<
    EntryRequirementsSearchProps
> = () => {
    // We will account for both tour code param formats for now, for historical content from all brands
    const [tourCode, setTourCode] = useQueryState("tourCode", {
        history: "push",
    });
    const [leagcyTourCode] = useQueryState("tour-code");
    const [query, setQuery] = useState<string | null>(null);
    const [searchResults, setSearchResults] = useState<SearchResults<{
        name: string;
        tourCode: string;
    }> | null>(null);

    if (tourCode || leagcyTourCode) {
        return null;
    }

    const search = async (value: string, pageSize: number) => {
        if (!value) {
            setSearchResults(null);
            setQuery(null);
            return;
        }

        searchHelper.setQuery(value);
        const results = await searchHelper.searchOnce({
            hitsPerPage: pageSize,
        });
        setSearchResults(results.content);
        setQuery(value);
    };

    let results: ReactNode = null;
    if (searchResults && searchResults.nbHits === 0) {
        results = (
            <Card className={styles.results}>
                <Text.Body>
                    Hmm, no tours match your search. Please try a different
                    search.
                </Text.Body>
            </Card>
        );
    }
    if (searchResults && searchResults.nbHits > 0) {
        results = (
            <Card className={styles.results}>
                <div>
                    {searchResults.hits.map((hit) => {
                        if (
                            !hit._highlightResult?.name?.value ||
                            !hit.tourCode
                        ) {
                            return null;
                        }
                        return (
                            <Text.Body
                                className={styles.result}
                                key={hit.objectID}
                                href={`?tourCode=${hit.tourCode}`}
                                onClick={(e) => {
                                    e.preventDefault();
                                    setTourCode(hit.tourCode);
                                }}
                                tag="a"
                            >
                                <Markdown
                                    markdown={hit._highlightResult.name.value}
                                />
                            </Text.Body>
                        );
                    })}
                </div>
                {searchResults.hitsPerPage < searchResults.nbHits &&
                    !!query && (
                        <div className={styles.button}>
                            <Button
                                onClick={() =>
                                    search(query, searchResults.nbHits)
                                }
                                variant="secondary"
                            >
                                See all {searchResults.nbHits} tours
                            </Button>
                        </div>
                    )}
            </Card>
        );
    }

    return (
        <Container width="small" spacing={{ xs: "0", sm: "0" }}>
            <Text.Body className={styles.heading}>
                Search for a tour to see entry requirements for each country
                you’ll visit
            </Text.Body>
            <SearchField
                initialValue={query || ""}
                onClear={() => setSearchResults(null)}
                onSearch={(value) => search(value, 10)}
                onTypingStopped={(value) => search(value, 10)}
                placeholder="Search by tour name"
            />
            {results}
        </Container>
    );
};
